'use client';

import { Fragment, useMemo, useState } from 'react';
import { Skeleton } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow
} from '@repo/ui/components/table';
import { TableShellHead } from '@repo/ui/components/table-shell';
import { track } from '@/lib/analytics';

import type { InvestorHolding, InvestorHoldingsResponse } from '../types/api';
import { chartColor } from '@/lib/chart-palette';
import {
  formatHoldingsTotal,
  holdingRowIds,
  shareColumnLabel,
  shareSuffix,
  valuationNotes,
  valueHoldings,
  WALLET_TRACKING_OFF,
  type CurrencyRegistry
} from '../utils/holding-format';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatPercent } from '@/lib/format/percent/percent';
import {
  buildEntries,
  buildGroups,
  idOf,
  POSITION_LENSES,
  positionsTruncationNote,
  segmentIdFor,
  shareOf,
  toggleId,
  usdLabel,
  usdOf,
  type PositionLens,
  type RowFacts
} from '../utils/owned-positions';
import type { LedgerRowContext } from './owned-positions-holding-row';
import {
  LedgerLoadingRows,
  LensPanel,
  LensPanelBody,
  LensPanelFooter,
  useLensSelection
} from '@/components/lens-panel';
import { GroupRow, LedgerTotalRow, MemberList } from './owned-positions-rows';
import { HoldingsUnavailable, NoHoldings } from './owned-positions-states';
import { memberArcStyles, PortfolioDonut } from '@/components/portfolio-donut';

interface OwnedPositionsProps {
  /**
   * The first page of holdings, largest first. The card describes these
   * rows and says so when the wallet holds more than one page.
   */
  holdings?: InvestorHoldingsResponse;
  /**
   * Names for strategy currency addresses, resolved by the view. A vault's
   * currency arrives as a contract address and is never printed as one.
   */
  currencies?: CurrencyRegistry;
  isLoading?: boolean;
  isError?: boolean;
  error?: unknown;
}

/**
 * Positions (PO2, node 1787:787): the single holdings section, layered so
 * the lens drives both chart and table. Layer one mirrors the donut — under
 * a grouped lens the table's top rows ARE the segments (chains, classes,
 * types) with aggregate value and share; the Position lens stays flat.
 * Layer two opens a group into its holdings; layer three opens a holding's
 * detail drawer (units, price and source, liquidity tier, asset class).
 * ROWA vault names navigate to the vault page. A lens with one group
 * auto-expands so the table never collapses to a single row.
 *
 * The card reads the first page of holdings, largest first. When the wallet
 * holds more than that page, the donut, the groups and the total row all
 * describe the listed rows and are labelled as such; the full list, with
 * its own pager, is the All positions table below.
 */
export function OwnedPositions({
  holdings,
  currencies,
  isLoading = false,
  isError = false,
  error
}: OwnedPositionsProps) {
  const [lens, setLens] = useState<PositionLens>('position');
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [revealedTails, setRevealedTails] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [detailId, setDetailId] = useState<string | null>(null);
  const selection = useLensSelection();

  const rows = useMemo(() => holdings?.holdings ?? [], [holdings]);
  const facts = useMemo<RowFacts>(
    () => ({
      ids: holdingRowIds(rows),
      valuation: holdings ? valueHoldings(holdings, currencies) : null,
      currencies,
      fx: holdings?.fx
    }),
    [rows, holdings, currencies]
  );
  const valuation = facts.valuation;

  const entries = useMemo(
    () => buildEntries(rows, lens, facts),
    [rows, lens, facts]
  );
  const segmentColorById = useMemo(
    () => new Map(entries.map((entry, index) => [entry.id, chartColor(index)])),
    [entries]
  );
  const groups = useMemo(
    () => (lens === 'position' ? [] : buildGroups(rows, lens, facts)),
    [rows, lens, facts]
  );

  // The portfolio total on the basis `valueHoldings` settled on: added up
  // here from converted rows when the whole wallet is listed, the served
  // number when it spans more than one page.
  const totalLabel = valuation ? formatHoldingsTotal(valuation) : '—';
  // The rows on this card. One page is not the portfolio when the wallet
  // holds more than a page, so those figures are stated for the listed rows.
  const isPartial = valuation != null && !valuation.isWholeWallet;
  const listed = useMemo(() => {
    let share = 0;
    for (const holding of rows) share += shareOf(facts, holding) ?? 0;
    return { usd: valuation?.listedUsd ?? 0, share };
  }, [rows, facts, valuation]);
  // Rows left out for lack of a rate, a served total that adds another
  // currency in unconverted, a stale rate: one plain sentence each.
  const notes = useMemo(
    () => (valuation ? valuationNotes(valuation) : []),
    [valuation]
  );
  const isEmpty =
    !isLoading && !isError && (holdings?.coverage.holdingsTotal ?? 0) === 0;
  const truncationNote = positionsTruncationNote(holdings);

  const changeLens = (nextLens: PositionLens) => {
    setLens(nextLens);
    setExpandedGroups(new Set());
    setRevealedTails(new Set());
    setDetailId(null);
    selection.setPreviewId(null);
    track('portfolio.lens_changed', {
      section: 'owned-positions',
      lens: nextLens
    });
  };

  const toggleGroup = (id: string) =>
    setExpandedGroups((current) => toggleId(current, id));

  // Each member row wears exactly its outer arc's shade so ring and rows
  // read as one structure.
  const memberDotById = useMemo(() => memberArcStyles(entries), [entries]);

  // Ids that exist on either sunburst ring — member rows sync to their own
  // outer arc when it exists, else their bucket, else the Others segment.
  const syncableIds = useMemo(() => {
    const ids = new Set<string>();
    for (const entry of entries) {
      ids.add(entry.id);
      entry.children?.forEach((child) => ids.add(child.id));
    }
    return ids;
  }, [entries]);

  const segmentSyncId = (holding: InvestorHolding): string | null => {
    const own = idOf(facts, holding);
    if (syncableIds.has(own)) return own;
    const raw = segmentIdFor(holding, lens, facts);
    if (syncableIds.has(raw)) return raw;
    return usdOf(facts, holding) != null && syncableIds.has('other')
      ? 'other'
      : null;
  };

  const toggleTail = (key: string) =>
    setRevealedTails((current) => toggleId(current, key));

  const dotColor = (segmentId: string | null): string =>
    segmentId
      ? (segmentColorById.get(segmentId) ?? 'var(--color-rowa-border-subtle)')
      : 'var(--color-rowa-border-subtle)';

  const rowContext: LedgerRowContext = {
    facts,
    detailId,
    onDetailChange: setDetailId,
    segmentSyncId,
    rowProps: selection.rowProps,
    dotColor,
    memberDotById
  };

  return (
    <LensPanel
      id="wallet-holdings"
      titleId="owned-positions-title"
      title="Positions"
      lenses={POSITION_LENSES}
      lens={lens}
      onLensChange={changeLens}
      lensAriaLabel="Position lens"
    >
      {isError ? (
        <HoldingsUnavailable error={error} />
      ) : isEmpty ? (
        <NoHoldings />
      ) : (
        <LensPanelBody
          donut={
            isLoading ? (
              <Skeleton className="mx-auto aspect-square w-full max-w-64 rounded-full" />
            ) : (
              <PortfolioDonut
                ariaLabel={`Positions by ${POSITION_LENSES.find((item) => item.value === lens)?.label.toLowerCase() ?? 'position'}`}
                segments={entries}
                // The centre describes what the ring is made of. With more
                // than one page of holdings the ring is the listed rows,
                // so the centre states their value, not the whole NAV.
                centerValue={isPartial ? usdLabel(listed.usd) : totalLabel}
                centerLabel={
                  isPartial
                    ? `Largest ${rows.length} holdings`
                    : valuation?.total.reconciled
                      ? 'Priced NAV'
                      : 'In dollars'
                }
                centerSub={
                  !holdings
                    ? undefined
                    : isPartial
                      ? // Shares of this page add to 100% of it: only the
                        // count says anything there.
                        valuation?.shareBasis === 'page'
                        ? `of ${holdings.total} holdings`
                        : `of ${holdings.total} · ${formatPercent(listed.share, { digits: 1 })} of ${valuation?.shareBasis === 'valued' ? 'the valued total' : 'NAV'}`
                      : `${holdings.coverage.pricedCount} holding${holdings.coverage.pricedCount === 1 ? '' : 's'} · ${formatBasisPoints(holdings.coverage.pricedBps, { fractionDigits: 0 })}`
                }
                popoverEyebrow="Position"
                shareLabel={shareSuffix(valuation)}
                previewId={selection.previewId}
                onPreview={selection.setPreviewId}
              />
            )
          }
        >
          <Table aria-label="Positions ledger">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableShellHead className="pl-4">
                  {POSITION_LENSES.find((item) => item.value === lens)?.label ??
                    'Position'}
                </TableShellHead>
                <TableShellHead align="right">Value</TableShellHead>
                {/* Shares of the portfolio only when every holding is
                    valued; otherwise shares of what could be valued. */}
                <TableShellHead align="right">
                  {shareColumnLabel(valuation)}
                </TableShellHead>
                <TableShellHead className="pr-4">
                  <span className="sr-only">State and details</span>
                </TableShellHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <LedgerLoadingRows columns={4} />
              ) : (
                <>
                  {lens === 'position' ? (
                    <MemberList
                      members={rows}
                      indent={false}
                      revealed={revealedTails.has('__flat')}
                      onToggleTail={() => toggleTail('__flat')}
                      context={rowContext}
                    />
                  ) : (
                    groups.map((group) => {
                      // One bucket needs no accordion — open it.
                      const groupOpen =
                        groups.length === 1 || expandedGroups.has(group.id);
                      const segmentId = segmentColorById.has(group.id)
                        ? group.id
                        : segmentColorById.has('other')
                          ? 'other'
                          : null;

                      return (
                        <Fragment key={group.id}>
                          <GroupRow
                            group={group}
                            open={groupOpen}
                            segmentId={segmentId}
                            onToggle={() => toggleGroup(group.id)}
                            context={rowContext}
                          />
                          {groupOpen ? (
                            <MemberList
                              members={group.members}
                              indent
                              revealed={revealedTails.has(group.id)}
                              onToggleTail={() => toggleTail(group.id)}
                              context={rowContext}
                            />
                          ) : null}
                        </Fragment>
                      );
                    })
                  )}
                  <LedgerTotalRow
                    isPartial={isPartial}
                    rowCount={rows.length}
                    valuation={valuation}
                    totalLabel={totalLabel}
                    listed={listed}
                  />
                </>
              )}
            </TableBody>
          </Table>
        </LensPanelBody>
      )}

      {!isLoading && !isError && !isEmpty && holdings ? (
        <LensPanelFooter
          note={
            <>
              Scope: {holdings.coverage.pricedCount} priced holding
              {holdings.coverage.pricedCount === 1 ? '' : 's'} of{' '}
              {holdings.total} · {totalLabel} ·{' '}
              {/* The contract drops external wallet rows whenever the scan
                  is not fresh — that scope change must never pass silently,
                  or a vaults-only response reads as the whole wallet. */}
              {holdings.coverage.externalIncluded
                ? 'valuation source: portfolio ledger'
                : holdings.coverage.externalStatus === 'disabled'
                  ? `ROWA positions only. ${WALLET_TRACKING_OFF}.`
                  : "ROWA positions only. We can't read your other wallet tokens right now."}
              {truncationNote}
              {isPartial
                ? ` This card shows your ${rows.length} largest holdings of ${holdings.total}. All positions below lists every one.`
                : ''}
              {notes.map((note) => ` ${note}`).join('')}
            </>
          }
        />
      ) : null}
    </LensPanel>
  );
}
